/**
 * Invoice payment terms — AR source of truth + customer-facing presentation.
 *
 * Authoritative commercial fields on invoices:
 * - invoice_date
 * - payment_terms_preset
 * - payment_terms_days
 * - due_date
 *
 * These feed future AR aging and payment reminders. Preview/PDF consume
 * buildPaymentTermsPresentation() and never assemble wording ad hoc.
 */

import { supabase } from "../supabase";
import { getEffectiveOrgSetting } from "./organizationSettings";
import { loadActorOrganizationSettings } from "./invoiceOrgSettings";
import { PAYMENT_TERMS_SECTION_TITLE } from "./invoiceCommercialSections";

export const DEFAULT_PAYMENT_TERMS_DAYS = 30;
export const DEFAULT_PAYMENT_TERMS_PRESET = "net_30";

/** Full registry used by Invoice Review (includes legacy Net 45 / Net 60). */
export const PAYMENT_TERMS_OPTIONS = Object.freeze([
  { value: "due_on_receipt", days: 0, label: "Due Upon Receipt" },
  { value: "net_7", days: 7, label: "Net 7" },
  { value: "net_15", days: 15, label: "Net 15" },
  { value: "net_30", days: 30, label: "Net 30" },
  { value: "net_45", days: 45, label: "Net 45" },
  { value: "net_60", days: 60, label: "Net 60" },
  {
    value: "due_on_specific_date",
    days: null,
    label: "Due on Specific Date",
    manualDueDate: true,
  },
  { value: "custom", days: null, label: "Custom", manualDueDate: true },
]);

/** Curated org-default picker (Admin). */
export const ORG_PAYMENT_TERMS_OPTIONS = Object.freeze(
  PAYMENT_TERMS_OPTIONS.filter((option) =>
    [
      "due_on_receipt",
      "net_7",
      "net_15",
      "net_30",
      "due_on_specific_date",
      "custom",
    ].includes(option.value),
  ),
);

/** @deprecated Prefer ORG_PAYMENT_TERMS_OPTIONS / PAYMENT_TERMS_OPTIONS. */
export const PAYMENT_TERMS_PRESET_OPTIONS = Object.freeze(
  ORG_PAYMENT_TERMS_OPTIONS.filter(
    (option) => option.days != null,
  ).map((option) => ({ value: option.days, label: option.label })),
);

export const PAYMENT_TERMS_PRESET_VALUES = Object.freeze(
  PAYMENT_TERMS_OPTIONS.map((option) => option.value),
);

export function todayIsoDate(now = new Date()) {
  return now.toISOString().slice(0, 10);
}

export function normalizeIsoDate(value) {
  if (!value) return null;
  const trimmed = String(value).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return null;
  }
  const parsed = new Date(`${trimmed}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return trimmed;
}

export function normalizePaymentTermsDays(value) {
  const days = Number(value);
  if (!Number.isFinite(days) || days < 0) {
    return DEFAULT_PAYMENT_TERMS_DAYS;
  }
  return days;
}

export function getPaymentTermsOption(preset) {
  return (
    PAYMENT_TERMS_OPTIONS.find((option) => option.value === preset) || null
  );
}

export function isManualDueDatePreset(preset) {
  const option = getPaymentTermsOption(preset);
  return Boolean(option?.manualDueDate);
}

export function resolvePaymentTermsPreset(invoiceOrPreset, days = null) {
  if (typeof invoiceOrPreset === "string") {
    const match = getPaymentTermsOption(invoiceOrPreset);
    return match ? match.value : "custom";
  }

  const preset = invoiceOrPreset?.payment_terms_preset;
  if (preset && getPaymentTermsOption(preset)) {
    return preset;
  }

  const terms =
    days != null
      ? Number(days)
      : Number(invoiceOrPreset?.payment_terms_days);
  if (!Number.isFinite(terms) || terms < 0) {
    return DEFAULT_PAYMENT_TERMS_PRESET;
  }

  const match = PAYMENT_TERMS_OPTIONS.find(
    (option) => option.days != null && option.days === terms,
  );
  return match ? match.value : "custom";
}

export function paymentTermsDaysForPreset(preset, fallbackDays = null) {
  const option = getPaymentTermsOption(preset);
  if (!option) {
    return normalizePaymentTermsDays(fallbackDays);
  }
  if (option.manualDueDate) {
    if (fallbackDays == null || fallbackDays === "") {
      return null;
    }
    return normalizePaymentTermsDays(fallbackDays);
  }
  return option.days;
}

export function formatPaymentTermsLabel(paymentTermsDaysOrInvoice) {
  if (
    paymentTermsDaysOrInvoice &&
    typeof paymentTermsDaysOrInvoice === "object"
  ) {
    const preset = resolvePaymentTermsPreset(paymentTermsDaysOrInvoice);
    const option = getPaymentTermsOption(preset);
    if (option) {
      return option.label;
    }
  }

  const days = Number(
    typeof paymentTermsDaysOrInvoice === "object"
      ? paymentTermsDaysOrInvoice?.payment_terms_days
      : paymentTermsDaysOrInvoice,
  );
  const match = PAYMENT_TERMS_OPTIONS.find(
    (option) => option.days != null && option.days === days,
  );
  if (match) {
    return match.label;
  }
  if (Number.isFinite(days) && days >= 0) {
    return days === 0 ? "Due Upon Receipt" : `Net ${days}`;
  }
  return `Net ${DEFAULT_PAYMENT_TERMS_DAYS}`;
}

/**
 * Due date = invoice date + net days (UTC date arithmetic).
 */
export function computeDueDateFromInvoiceDate(
  invoiceDate,
  paymentTermsDays = DEFAULT_PAYMENT_TERMS_DAYS,
) {
  const baseIso = normalizeIsoDate(invoiceDate);
  if (!baseIso) {
    throw new Error("Invalid invoice date.");
  }

  const base = new Date(`${baseIso}T00:00:00.000Z`);
  const days = Number(paymentTermsDays);
  const terms =
    Number.isFinite(days) && days >= 0 ? days : DEFAULT_PAYMENT_TERMS_DAYS;
  base.setUTCDate(base.getUTCDate() + terms);
  return base.toISOString().slice(0, 10);
}

/** @deprecated Prefer computeDueDateFromInvoiceDate — sent_at was the legacy invoice date. */
export function computeDueDateFromSent(
  sentDate,
  paymentTermsDays = DEFAULT_PAYMENT_TERMS_DAYS,
) {
  const base =
    sentDate instanceof Date ? new Date(sentDate) : new Date(sentDate);
  if (Number.isNaN(base.getTime())) {
    throw new Error("Invalid sent date.");
  }
  return computeDueDateFromInvoiceDate(
    base.toISOString().slice(0, 10),
    paymentTermsDays,
  );
}

export function resolveInvoiceDate(invoice, { fallbackToToday = false } = {}) {
  const explicit = normalizeIsoDate(invoice?.invoice_date);
  if (explicit) {
    return explicit;
  }
  if (invoice?.sent_at) {
    return String(invoice.sent_at).slice(0, 10);
  }
  if (invoice?.seeded_at) {
    return String(invoice.seeded_at).slice(0, 10);
  }
  if (invoice?.created_at) {
    return String(invoice.created_at).slice(0, 10);
  }
  return fallbackToToday ? todayIsoDate() : null;
}

export function resolveInvoiceDueDate(invoice) {
  const explicit = normalizeIsoDate(invoice?.due_date);
  if (explicit) {
    return explicit;
  }

  const invoiceDate = resolveInvoiceDate(invoice);
  if (!invoiceDate) {
    return null;
  }

  const terms = Number(invoice.payment_terms_days);
  const paymentTermsDays =
    Number.isFinite(terms) && terms >= 0 ? terms : DEFAULT_PAYMENT_TERMS_DAYS;

  return computeDueDateFromInvoiceDate(invoiceDate, paymentTermsDays);
}

/**
 * Build draft payment fields when the user changes preset / dates.
 */
export function buildPaymentTermsState({
  preset,
  invoiceDate,
  dueDate,
  previousDays = null,
}) {
  const resolvedPreset = resolvePaymentTermsPreset(preset);
  const normalizedInvoiceDate =
    normalizeIsoDate(invoiceDate) || todayIsoDate();
  const isManualDue = isManualDueDatePreset(resolvedPreset);
  const days = paymentTermsDaysForPreset(resolvedPreset, previousDays);

  let nextDueDate = normalizeIsoDate(dueDate);
  if (!isManualDue) {
    nextDueDate = computeDueDateFromInvoiceDate(
      normalizedInvoiceDate,
      days ?? DEFAULT_PAYMENT_TERMS_DAYS,
    );
  } else if (!nextDueDate) {
    nextDueDate = computeDueDateFromInvoiceDate(
      normalizedInvoiceDate,
      previousDays ?? DEFAULT_PAYMENT_TERMS_DAYS,
    );
  }

  return {
    payment_terms_preset: resolvedPreset,
    payment_terms_days: isManualDue
      ? daysFromDateRange(normalizedInvoiceDate, nextDueDate)
      : days,
    invoice_date: normalizedInvoiceDate,
    due_date: nextDueDate,
    isCustom: isManualDue,
    isManualDueDate: isManualDue,
  };
}

export function daysFromDateRange(startIso, endIso) {
  const start = normalizeIsoDate(startIso);
  const end = normalizeIsoDate(endIso);
  if (!start || !end) {
    return null;
  }
  const startDate = new Date(`${start}T00:00:00.000Z`);
  const endDate = new Date(`${end}T00:00:00.000Z`);
  const diffMs = endDate.getTime() - startDate.getTime();
  return Math.max(0, Math.round(diffMs / (24 * 60 * 60 * 1000)));
}

function formatDueDateLabel(isoDate) {
  const normalized = normalizeIsoDate(isoDate);
  if (!normalized) return "—";
  // Parse as local calendar date to avoid UTC midnight shifting the day.
  const [year, month, day] = normalized.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Structured presentation for Preview/PDF. Returns null when nothing useful
 * can be shown (backward compatible omit).
 */
export function buildPaymentTermsPresentation(invoice, options = {}) {
  if (!invoice) return null;

  const preset = resolvePaymentTermsPreset(invoice);
  const dueDate = resolveInvoiceDueDate(invoice);
  const option = getPaymentTermsOption(preset);
  const blocks = [];

  if (preset === "due_on_specific_date") {
    if (dueDate) {
      blocks.push({
        type: "field",
        label: "Payment Due",
        value: formatDueDateLabel(dueDate),
      });
    }
  } else if (preset === "custom") {
    blocks.push({
      type: "field",
      label: "Payment Terms",
      value: option?.label || "Custom",
    });
    if (dueDate) {
      blocks.push({
        type: "field",
        label: "Payment Due",
        value: formatDueDateLabel(dueDate),
      });
    }
  } else if (option) {
    blocks.push({
      type: "field",
      label: "Payment Terms",
      value: option.label,
    });
  } else if (dueDate) {
    blocks.push({
      type: "field",
      label: "Payment Due",
      value: formatDueDateLabel(dueDate),
    });
  }

  if (blocks.length === 0) {
    return null;
  }

  return {
    title: options.title || PAYMENT_TERMS_SECTION_TITLE,
    preset,
    dueDate,
    paymentTermsDays:
      invoice.payment_terms_days == null
        ? null
        : Number(invoice.payment_terms_days),
    blocks,
  };
}

export function getOrganizationPaymentTermsConfig(settings) {
  const presetRaw = getEffectiveOrgSetting(
    settings,
    "invoices.default_payment_terms_preset",
    null,
  );
  const daysRaw = getEffectiveOrgSetting(
    settings,
    "invoices.default_payment_terms_days",
    DEFAULT_PAYMENT_TERMS_DAYS,
  );

  const preset = presetRaw
    ? resolvePaymentTermsPreset(String(presetRaw), daysRaw)
    : resolvePaymentTermsPreset(null, daysRaw);
  const days = paymentTermsDaysForPreset(preset, daysRaw);

  return {
    preset,
    days: days == null ? null : normalizePaymentTermsDays(days),
  };
}

export async function loadDefaultPaymentTermsDays(actor, client = supabase) {
  try {
    const config = await loadDefaultPaymentTermsConfig(actor, client);
    if (config.days == null) {
      return DEFAULT_PAYMENT_TERMS_DAYS;
    }
    return config.days;
  } catch (err) {
    console.warn("[invoicePaymentTerms] default terms fallback", err);
    return DEFAULT_PAYMENT_TERMS_DAYS;
  }
}

export async function loadDefaultPaymentTermsPreset(actor, client = supabase) {
  const config = await loadDefaultPaymentTermsConfig(actor, client);
  return config.preset;
}

export async function loadDefaultPaymentTermsConfig(
  actor,
  client = supabase,
) {
  try {
    const settings = await loadActorOrganizationSettings(actor, client);
    return getOrganizationPaymentTermsConfig(settings);
  } catch (err) {
    console.warn("[invoicePaymentTerms] default config fallback", err);
    return {
      preset: DEFAULT_PAYMENT_TERMS_PRESET,
      days: DEFAULT_PAYMENT_TERMS_DAYS,
    };
  }
}

/**
 * Resolve effective payment-terms fields for a draft invoice.
 */
export function resolveInvoicePaymentTermsFields({
  invoice = null,
  organizationPaymentTerms = null,
  invoiceDate = null,
} = {}) {
  const override = invoice?.payment_terms_override === true;
  if (override) {
    const state = buildPaymentTermsState({
      preset: resolvePaymentTermsPreset(invoice),
      invoiceDate:
        invoiceDate ||
        resolveInvoiceDate(invoice, { fallbackToToday: true }),
      dueDate: invoice?.due_date,
      previousDays: invoice?.payment_terms_days,
    });
    return state;
  }

  const org =
    organizationPaymentTerms ||
    ({
      preset: DEFAULT_PAYMENT_TERMS_PRESET,
      days: DEFAULT_PAYMENT_TERMS_DAYS,
    });

  return buildPaymentTermsState({
    preset: org.preset || DEFAULT_PAYMENT_TERMS_PRESET,
    invoiceDate:
      invoiceDate ||
      resolveInvoiceDate(invoice, { fallbackToToday: true }) ||
      todayIsoDate(),
    dueDate: null,
    previousDays: org.days,
  });
}
